import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  UserPermission
} from '@globaleris/ui.library'
import { Card, CardContent, CardHeader, Grid, makeStyles, Paper, useTheme } from '@material-ui/core'
import CloudDownloadRoundedIcon from '@material-ui/icons/CloudDownloadRounded'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumGrupoDocumento } from '../../api/oData/enums'
import { EnumFileStateWithDispatch } from '../../api/Pioneer/enums/enumFileStateWithDispatch'
import { enumToSelectOptionArray } from '../../helpers'
import { generateIconsFromConfiguration } from '../../helpers/dataTableActionIconHelper'
import { fileDownloadHelper } from '../../helpers/fileDownloadHelper'
import { ActionIcons } from '../../model'
import { regulatoryReportDownloadCard } from './regulatoryReports.Model'
import {
  downloadCoresReportFile,
  fetchReportFilesByGroupsListItems,
  filterApplicableActionsConfigurationRemit
} from './regulatoryReports.service'
import { styles } from './regulatoryReports.styles'

export interface RegulatoryReportsPowerDownloadCardProps {
  onFetching: (isFetching: boolean) => void
  refreshChild: boolean
  groupDocumentToFilter: EnumGrupoDocumento[]
}

export const RegulatoryReportsPowerDownloadCard: React.FC<
  RegulatoryReportsPowerDownloadCardProps
> = props => {
  const [t] = useTranslation(['regulatoryReports', 'enums', 'storageHelper'])
  const [dataDownloadCard, setdataDownloadCard] = useState<
    regulatoryReportDownloadCard[] | undefined
  >([])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  // const storageContainer: string = `hd-${getAppConfigurationValue(
  //   AppConfigurationKey.ContainerRegulatoryReporting
  // )}`

  React.useEffect(() => {
    const fetchData = () => {
      props.onFetching(true)
      fetchRegulatoryReportFilesPower()
    }

    fetchData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.refreshChild])

  const completeDataTableActions = (
    item: regulatoryReportDownloadCard
  ): regulatoryReportDownloadCard => {
    return {
      ...item,
      actions:
        item.actionsConfiguration &&
        item.actionsConfiguration.length > 0 &&
        generateIconsFromConfiguration(
          item,
          filterApplicableActionsConfigurationRemit(item, item.actionsConfiguration),
          new Map<ActionIcons, Function>([])
        )
    }
  }

  const fetchRegulatoryReportFilesPower = async (): Promise<
    regulatoryReportDownloadCard[] | null
  > => {
    // const groupDocumentToFilter = [
    //   enumValueToKey(EnumGrupoDocumento, EnumGrupoDocumento.InformesRegulatoriosPower)
    // ];
    return await fetchReportFilesByGroupsListItems(props.groupDocumentToFilter).then(
      async response => {
        if (response.success && response.data?.length > 0) {
          setdataDownloadCard(
            response.data?.map((item: regulatoryReportDownloadCard) => {
              return completeDataTableActions(item)
            })
          )
          let updatedReportFiles: regulatoryReportDownloadCard[] = []
          response.data!.forEach((pendingReport: regulatoryReportDownloadCard) => {
            if (pendingReport.fileState === EnumFileStateWithDispatch.EnProceso) {
              updatedReportFiles.push(pendingReport)
            }
          })
          props.onFetching(false)
          return updatedReportFiles
        } else {
          props.onFetching(false)
          setdataDownloadCard([])
          return null
        }
      }
    )
  }

  //#region Handlers

  const onDownloadHandler = async (row: regulatoryReportDownloadCard) => {
    const response = await downloadCoresReportFile(row)
    if (response.success && response.data) {
      fileDownloadHelper(response.data.name, response.data)
    }
  }

  //#endregion

  //#region DataTable definition

  const columns: DataTableColumn[] = [
    {
      name: 'documentType',
      label: t('regulatoryReports:commodityPower.table.colType'),
      type: DataType.TEXT
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML
    },
    {
      name: 'fileName',
      label: t('regulatoryReports:commodityPower.table.colName'),
      type: DataType.TEXT
    },
    {
      name: 'fileDate',
      label: t('regulatoryReports:commodityPower.table.colDate'),
      type: DataType.DATE
    },
    {
      name: 'fileState',
      label: t('regulatoryReports:commodityPower.table.colState'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumFileStateWithDispatch,
        'enums:EnumFileStateWithDispatch'
      )
    }
  ]

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: false,
    print: false,
    selectableRows: false,
    canDelete: false,
    canEdit: false,
    rowCustomActions: [
      {
        userPermission: UserPermission.READ_ONLY,
        icon: <CloudDownloadRoundedIcon fontSize="small" />,
        tooltip: t('regulatoryReports:regulatoryReportsDownloadCard.actions.download'),
        onClick: onDownloadHandler,
        visibleIf: 'canDownload'
      }
    ],
    selectToolbarCustomActions: []
  }

  //#endregion

  return (
    <div className={classes.container}>
      <Grid container>
        <Grid item lg={12}>
          <Paper elevation={6}>
            <Card>
              <CardHeader
                title={t('regulatoryReports:commodityPower.table.tableTitle')}
              ></CardHeader>
              <CardContent>
                <DataTable data={dataDownloadCard} columns={columns} options={options} />
              </CardContent>
            </Card>
          </Paper>
        </Grid>
      </Grid>
    </div>
  )
}
